import {Vector3} from './types.ts';
import {SceneFrameHandle} from './frame/SceneFrame.tsx';

// postMessage channel between the Scene Builder editor and the scene iframe.
// The iframe is sandboxed with an opaque origin, so messages to it can't name a
// targetOrigin ('*'; they're addressed to that one window), and messages from
// it are accepted only from that window, sent from the opaque origin "null".
export const SCENE_MESSAGE_SOURCE = 'mixiply-scene';

export type GizmoMode = 'translate' | 'rotate' | 'scale';

// Editor (parent) -> scene (iframe). The `source` tag is added on the wire by
// postToScene; keeping the payloads source-free avoids the union Omit collapse.
export type EditorToScenePayload =
    | {action: 'ping'}
    | {action: 'enableEditing'}
    | {action: 'highlight'; elementId: string | null}
    | {action: 'select'; elementId: string | null}
    | {action: 'setGizmoMode'; mode: GizmoMode}
    | {action: 'getCamera'};

// Where the scene's camera is now: the reply to getCamera. fov in radians.
export type SceneCameraState = {
    position: Vector3;
    target: Vector3;
    fov: number;
};

// Scene (iframe) -> editor (parent)
export type SceneToEditorMessage =
    | {source: typeof SCENE_MESSAGE_SOURCE; action: 'ready'}
    | {
          source: typeof SCENE_MESSAGE_SOURCE;
          action: 'selected';
          elementId: string;
      }
    | {
          source: typeof SCENE_MESSAGE_SOURCE;
          action: 'transform';
          elementId: string;
          position?: Vector3;
          rotation?: Vector3;
          scaling?: Vector3;
      }
    | ({
          source: typeof SCENE_MESSAGE_SOURCE;
          action: 'cameraState';
      } & SceneCameraState);

export function postToScene(
    frame: SceneFrameHandle | null,
    message: EditorToScenePayload
) {
    frame?.contentWindow?.postMessage(
        {source: SCENE_MESSAGE_SOURCE, ...message},
        '*'
    );
}

export function parseSceneMessage(
    event: MessageEvent,
    frame: SceneFrameHandle | null
): SceneToEditorMessage | null {
    if (
        event.origin !== 'null' ||
        !event.source ||
        event.source !== frame?.contentWindow
    ) {
        return null;
    }

    const data = event.data;
    if (
        data &&
        typeof data === 'object' &&
        (data as {source?: string}).source === SCENE_MESSAGE_SOURCE
    ) {
        return data as SceneToEditorMessage;
    }

    return null;
}
